import { ConversionAttribution, readConversionAttribution } from './ConversionTracking';

// First-touch attribution sent with the app sitting: the campaign, referrer and
// landing path the conversion capture already holds. The server keeps the first
// value each sitting reports, and the data lake reads a user's earliest sitting
// that has one. This module only reads that capture; it never captures or
// reports anything itself.

// The longest text the server stores on a sitting; it cuts longer values.
export const MAX_SITTING_ATTRIBUTION_TEXT = 512;

export type SittingAttribution = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  referrer?: string;
  landing_path?: string;
};

const textOf = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0
    ? value.trim().slice(0, MAX_SITTING_ATTRIBUTION_TEXT)
    : undefined;

// A campaign parameter by name, whatever its case in the landing address.
const campaignValue = (campaign: Record<string, string>, name: string): string | undefined =>
  textOf(Object.entries(campaign).find(([parameter]) => parameter.toLowerCase() === name)?.[1]);

// The path of the captured landing page, with no query string or fragment. The
// capture has already cut the query down to campaign parameters, and those are
// sent in their own fields.
const landingPathOf = (landingPage: unknown): string | undefined => {
  if (typeof landingPage !== 'string' || landingPage.length === 0) return undefined;
  try {
    return textOf(new URL(landingPage).pathname);
  } catch {
    return undefined;
  }
};

/** The sitting's attribution fields from a captured attribution; none without consent. */
export const sittingAttributionOf = (
  attribution: ConversionAttribution | undefined,
): SittingAttribution => {
  if (!attribution || attribution.tracking_allowed !== true) return {};
  const campaign =
    attribution.campaign && typeof attribution.campaign === 'object' ? attribution.campaign : {};
  const fields: SittingAttribution = {
    utm_source: campaignValue(campaign, 'utm_source'),
    utm_medium: campaignValue(campaign, 'utm_medium'),
    utm_campaign: campaignValue(campaign, 'utm_campaign'),
    referrer: textOf(attribution.referrer),
    landing_path: landingPathOf(attribution.landing_page),
  };
  return Object.fromEntries(
    Object.entries(fields).filter(([, value]) => value !== undefined),
  ) as SittingAttribution;
};

/** The attribution fields for an event's sitting, read from the stored capture. */
export const getSittingAttribution = (): SittingAttribution =>
  sittingAttributionOf(readConversionAttribution());
